import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Hint, Text, useResponsive } from '@common-fe/common-fe';

import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import { UpdatedProviderDto, UpdateProviderDto } from '@/modules/UseMyMoney/queries';
import { OcrProviderOption, USE_MY_MONEY_UPLOAD_STORE_KEY } from '@/modules/UseMyMoney/UseMyMoney.types';
import { useAddressOptions, useChangeProviderAddress } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';
import { getFullAddress } from '@/utils';

import FieldSetupLabel from '../../FieldSetupLabel/FieldSetupLabel';
import FieldSetupOptions from '../../FieldSetupOptions/FieldSetupOptions';

import ProviderAddressEmptyState from './ProviderAddressEmptyState';

interface Props {
  provider: OcrProviderOption;
  updateProvider: (createProviderDto: UpdateProviderDto) => Promise<UpdatedProviderDto>;
  handleEditProvider: (providerId?: string) => void;
}

const ProviderAddress: React.FC<Props> = ({ provider, updateProvider, handleEditProvider }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { addressOptions, activeAddressOption, providerAddressKey } = useAddressOptions({ provider });
  const { handleChangeProviderAddress } = useChangeProviderAddress({ provider, updateProvider });
  const { values: { documentId } } = useFilesInDocumentStore(USE_MY_MONEY_UPLOAD_STORE_KEY);

  const hasProviderAddress = !!provider?.address?.line1;

  const addressContent = useMemo(() => {
    if (addressOptions?.length && activeAddressOption) {
      return (
        <FieldSetupOptions
          testId="provider-address"
          value={providerAddressKey}
          onChangeValue={handleChangeProviderAddress}
          options={addressOptions}
          direction="column"
        />
      );
    }

    if (provider?.address?.line1) {
      return (
        <Hint
          testId="provider-address_hint"
          hintWidth={220}
          hintAlign={isMobile ? 'bottom' : 'right'}
          hintElement={(
            <Box
              data-testId="provider-address_title"
              pad={{ horizontal: 'spacing16', vertical: '10px' }}
              background="border1"
              round="button1Round"
              direction="row"
              align="center"
              justify="center"
            >
              <Text color="textDisabled">
                {getFullAddress(provider?.address)}
              </Text>
            </Box>
          )}
        >
          <Text size="small">
            {t('This address is tied to the provider selected. To change the address, select another provider or add a new one.')}
          </Text>
        </Hint>
      );
    }

    return (
      <ProviderAddressEmptyState
        provider={provider}
        handleEditProvider={handleEditProvider}
      />
    );
  }, [
    t,
    provider,
    addressOptions,
    handleEditProvider,
    handleChangeProviderAddress,
    isMobile,
    activeAddressOption,
    providerAddressKey,
  ]);

  return (
    <Box>
      <FieldSetupLabel
        testId="provider-address"
        title={t('Where do we send the payment?')}
        titleProps={{ size: '2xl' }}
        subTitle={hasProviderAddress ? t(`
          Address entered should be the provider's billing address.
          This may differ from their physical location.
          If you are unsure which address to select, in most cases, the providers billing address is a P.O Box.
        `) : undefined}
        isMobile={isMobile}
        hasValue={hasProviderAddress}
      />

      <Box
        margin={{ top: hasProviderAddress ? 'spacing24' : 'spacing12' }}
        alignContent="start"
      >
        {hasProviderAddress ? (
          <Text weight={700}>
            {documentId 
            ? t('Addresses found on your document')
            : t('Previously used addresses')}
          </Text>
        ) : null}

        <Box
          margin={{ top: hasProviderAddress ? 'spacing8' : undefined }}
          align="start"
        >
          {addressContent}
        </Box>
      </Box>
    </Box>
  );
};

export default ProviderAddress;
